/* Open the cart drawer after an add-to-cart the theme did not perform itself.

   product-form.js only opens the drawer from inside its own submit handler: it asks
   /cart/add for the drawer sections and then calls cartDrawer.renderContents(), which
   opens it. Anything that adds to the cart by another route therefore leaves the drawer
   shut, and the customer gets no confirmation that anything happened.

   The SellEasy upsell app is one such route. It binds a capture-phase click listener to
   button[name='add'] and calls preventDefault() + stopImmediatePropagation(), so the
   theme's submit handler never runs; the app shows its offer and completes the add on its
   own terms. Rather than reverse-engineering when it hands control back, this watches the
   cart itself: any completed /cart/add, whoever made it, opens the drawer.

   Deliberately additive and fail-safe — it patches fetch/XHR only to observe, never to
   block, and does nothing at all when the drawer is already open or the theme has just
   handled the add. */
(function () {
  'use strict';

  var DEBOUNCE_MS = 450; /* long enough for product-form's own render to win the race */
  var timer = null;
  var themeHandled = false;
  var changeSequence = 0;

  /* Buy Now adds to the cart and then leaves for the checkout. Its add looks exactly like
     any other from here, so without a way to say "this one is on its way out" the drawer
     opens over the top of a page that is already navigating away — and, worse, the pending
     timer survives in the back/forward cache and fires when the customer returns from the
     checkout, so the drawer springs open unasked. Whoever starts such an add sets this
     flag; it lives on window so load order between this file and its callers cannot
     matter. */
  function leavingForCheckout() {
    return !!(window.FIPDP && window.FIPDP.skipDrawerOpen);
  }

  /* fi-care-plan-tier.js moves the care plan to another tier as the basket's sofa total
     changes, and a tier move is a remove plus an add — /cart/change.js can only change a
     line's quantity. That add looks exactly like any other from here, so without this the
     drawer would fly open at a moment the customer did nothing, and on the cart page the
     openDrawer path below would reload the page under them.

     A counter rather than a flag, because two tier moves can overlap and the first to
     finish must not clear the second's suppression. Cleared by whoever set it. */
  function suppressed() {
    return !!(window.FIPDP && window.FIPDP.suppressDrawerOpen);
  }

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  function isAddRequest(url) {
    if (!url) return false;
    var s = String(url);
    return s.indexOf('/cart/add') !== -1;
  }

  function isChangeRequest(url) {
    if (!url) return false;
    var s = String(url);
    return s.indexOf('/cart/change') !== -1 || s.indexOf('/cart/update') !== -1;
  }

  /* Removing a line reports "There was an error while updating your bag" even though the
     line is removed. Verified against the live store: /cart/change.js answers 200 with
     item_count 0, no errors, and both drawer sections rendered correctly — so the request
     succeeded and something threw afterwards, inside the theme's own then-handler. Every
     part of that handler was checked (the .cart-item class, the Drawer-quantity id, the
     #CartDrawer and #cart-icon-bubble targets, .drawer__inner-empty, the section keys and
     their selectors) and all of them are correct, which points at a third-party script on
     the page rather than the theme.

     Whatever the cause, the message is untrue, so this repaints the drawer from the
     response Shopify actually returned and clears it. It is deliberately conditional on
     the server reporting success: a genuine failure still shows its error. */
  function repairAfterChange(state) {
    var el = drawer();
    if (!el || !state || !state.sections || !state.sections['cart-drawer']) return;

    try {
      el.renderContents(state);
    } catch (e) {
      return; /* leave the drawer alone rather than clear errors on a half-render */
    }

    var after = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (after) after.textContent = '';
  }

  /* product-form announces its own adds. When it does, it is already rendering and
     opening the drawer, so this must stay out of the way. */
  function watchThemeAdds() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      setTimeout(watchThemeAdds, 300);
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'product-form') themeHandled = true;
    });
  }

  function openDrawer() {
    timer = null;

    if (leavingForCheckout()) return;
    if (suppressed()) return;

    /* Restored from the back/forward cache, or the tab is in the middle of unloading —
       either way there is no one looking at this page to confirm anything to. */
    if (document.visibilityState === 'hidden') return;

    var el = drawer();
    if (!el || typeof el.renderContents !== 'function') return;

    /* The theme got there first, or the customer already has it open. */
    if (themeHandled) { themeHandled = false; return; }
    if (el.classList.contains('active')) return;

    /* On the cart page itself there is no drawer to open — the page needs the new lines. */
    if (window.location.pathname.indexOf('/cart') === 0) {
      window.location.reload();
      return;
    }

    var ids;
    try {
      ids = el.getSectionsToRender().map(function (section) { return section.id; });
    } catch (e) {
      return;
    }
    if (!ids.length) return;

    Promise.all([
      fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then(function (r) { return r.json(); }),
      fetch(window.location.pathname + '?sections=' + ids.join(','), { cache: 'no-store' })
        .then(function (r) { return r.json(); })
    ]).then(function (results) {
      var state = results[0];
      state.sections = results[1];
      try {
        el.renderContents(state); /* renderContents opens the drawer itself */
      } catch (e) {
        /* leave the page alone rather than half-rendering it */
      }
    }).catch(function () {});
  }

  function clearScheduledOpen() {
    clearTimeout(timer);
    timer = null;
  }

  function schedule() {
    clearScheduledOpen();
    if (leavingForCheckout()) return;
    if (suppressed()) return;
    timer = setTimeout(openDrawer, DEBOUNCE_MS);
  }

  /* pagehide covers both a real unload and being frozen into the back/forward cache, so
     dropping the timer here is what stops the drawer opening on the way to the checkout
     and again on the way back from it. */
  window.addEventListener('pagehide', clearScheduledOpen);

  /* ---- observe fetch ---- */
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      var url = input && (input.url || input);
      /* Must be invoked on window. A bare fetch() from strict-mode code arrives with an
         undefined `this`, and passing that straight through makes the browser reject the
         call outright ("Illegal invocation") — which would break every caller this file
         was only ever meant to watch. */
      var result = nativeFetch.apply(window, arguments);
      if (result && typeof result.then === 'function') {
        if (isAddRequest(url)) {
          result.then(function (response) {
            if (response && response.ok) schedule();
          }, function () {});
        } else if (isChangeRequest(url)) {
          clearScheduledOpen();
          var sequence = ++changeSequence;
          result.then(function (response) {
            if (!response || !response.ok || typeof response.clone !== 'function') return;
            /* clone so the theme still gets an unread body to work with */
            response
              .clone()
              .json()
              .then(function (state) {
                if (!state || state.errors) return;
                setTimeout(function () {
                  if (sequence === changeSequence) repairAfterChange(state);
                }, 80);
              })
              .catch(function () {});
          }, function () {});
        }
      }
      return result;
    };
  }

  /* ---- observe XHR (SellEasy patches XHR itself, so adds may arrive this way) ---- */
  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isAddRequest(url)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) schedule();
      });
    }
    return nativeOpen.apply(this, arguments);
  };

  watchThemeAdds();
})();
